
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdOutlinePersonOutline, MdOutlinePhone, MdLockOutline, MdVisibility, MdVisibilityOff, MdClose } from "react-icons/md";
import { authAPI } from "../../services/api"; // HATA ALMAMAK İÇİN RELATİF PATH KULLANDIM

// --- Yardımcılar ---
const passwordStrength = (pwd) => {
    if (!pwd) return null;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) score++;
    const labels = ["Çok zayıf", "Zayıf", "Orta", "İyi", "Çok iyi"];
    return { score, label: labels[score] };
};

const sanitizePhone = (v) => v.replace(/[^\d]/g, "").slice(0, 10);

const countries = [
    { name: "Türkiye", code: "+90", flag: "🇹🇷", sample: "5xx xxx xxxx" },
    { name: "Almanya", code: "+49", flag: "🇩🇪", sample: "15x xxxx xxxx" },
    { name: "ABD", code: "+1", flag: "🇺🇸", sample: "201 xxx xxxx" },
    { name: "İngiltere", code: "+44", flag: "🇬🇧", sample: "7400 xxxxxx" },
    { name: "Rusya", code: "+7", flag: "🇷🇺", sample: "9xx xxx xxxx" },
    { name: "Suudi Arabistan", code: "+966", flag: "🇸🇦", sample: "5x xxx xxxx" },
];

export default function Register() {
    const navigate = useNavigate();

    // --- State ---
    const [formData, setFormData] = useState({
        name: "",
        surname: "",
        phone: "",
        password: "",
        password_confirmation: "",
        role: "customer",
    });
    const [countryCode, setCountryCode] = useState("+90");
    const [agree, setAgree] = useState(false);
    const [marketing, setMarketing] = useState(true);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Toast ve Modal State
    const [toasts, setToasts] = useState([]);
    const [policyModal, setPolicyModal] = useState({ open: false, type: null }); // 'terms' | 'privacy'

    // --- Validasyonlar ---
    const strength = useMemo(() => passwordStrength(formData.password), [formData.password]);
    const phoneValid = /^[5][0-9]{9}$/.test(formData.phone);
    const passwdValid = formData.password.length >= 6 && formData.password === formData.password_confirmation;
    const formValid = formData.name.trim().length >= 2 && formData.surname.trim().length >= 2 && phoneValid && passwdValid && agree;

    // --- Fonksiyonlar ---
    const pushToast = (title, body) => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, title, body }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 4000); // 4 saniye sonra kaybolur
    };

    const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    const handlePhoneChange = (e) => setFormData((prev) => ({ ...prev, phone: sanitizePhone(e.target.value) }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!formValid) {
            setError("Lütfen zorunlu alanları doğru biçimde doldurun.");
            return;
        }
        setLoading(true);
        try {
            const dataToSend = { ...formData, phone: countryCode + formData.phone };
            await authAPI.register(dataToSend);
            pushToast("Kayıt Tamamlandı", "Hesabınız oluşturuldu. Giriş ekranına yönlendiriliyorsunuz…");
            setTimeout(() => navigate("/auth/sign-in"), 1500);
        } catch (err) {
            const errorMsg = err?.response?.data?.errors
                ? Object.values(err.response.data.errors).flat().join(", ")
                : err?.response?.data?.message || "Kayıt sırasında beklenmeyen bir sorun oluştu.";
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const placeholder = countries.find((c) => c.code === countryCode)?.sample || "5xx xxx xxxx";

    // --- Modal İçerikleri ---
    const renderPolicyTitle = policyModal.type === "privacy" ? "Gizlilik Politikası" : "Kullanım Koşulları";
    const renderPolicyBody = policyModal.type === "privacy" ? (
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p><strong className="text-navy-700 dark:text-white">Veri Sorumlusu:</strong> RandevUpp</p>
            <p>Bu uygulamada; hesap oluşturma, randevu süreçleri ve müşteri desteği için ad, soyad, telefon bilgileri işlenebilir. Veriler yalnızca hizmet sunumu, mevzuata uygunluk ve güvenlik amaçlarıyla saklanır.</p>
            <p><strong className="text-navy-700 dark:text-white">Haklarınız:</strong> Verilere erişme, düzeltme/silme, işlemeyi kısıtlama ve itiraz etme. Talepleriniz için uygulama içi destekten bize ulaşabilirsiniz.</p>
        </div>
    ) : (
        <div className="text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p>Bu uygulamayı kullanarak; doğru bilgi verme, hesabınızı koruma, yasadışı/zararlı içerik oluşturmama ve hizmeti kötüye kullanmama yükümlülüklerini kabul etmiş olursunuz.</p>
            <p>RandevUpp; bakım, güncelleme veya güvenlik nedenleriyle hizmette değişiklik yapma ya da hizmeti geçici olarak durdurma hakkını saklı tutar.</p>
            <p>İhlal şüphesi durumunda hesabınız kısıtlanabilir. Güncel metni düzenli olarak kontrol etmenizi rica ederiz.</p>
        </div>
    );

    return (
        <div className="mt-10 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
            <div className="w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[480px]">

                {/* Başlık Alanı */}
                <h4 className="mb-2.5 text-4xl font-bold text-navy-700 dark:text-white">Hesap Oluşturun</h4>
                <p className="mb-8 ml-1 text-base text-gray-600 dark:text-gray-400">Yeni hesabınızı oluşturun ve hemen başlayın.</p>

                {error && (
                    <div className="mb-4 flex items-center rounded-xl bg-red-100 p-3 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        <span className="mr-2 text-lg">⚠️</span> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="w-full">
                    {/* Ad Soyad */}
                    <div className="mb-4 grid grid-cols-2 gap-4">
                        <div>
                            <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Ad</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"><MdOutlinePersonOutline className="h-5 w-5" /></div>
                                <input type="text" name="name" placeholder="Adınız" value={formData.name} onChange={handleChange} disabled={loading} required className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20" />
                            </div>
                        </div>
                        <div>
                            <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Soyad</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"><MdOutlinePersonOutline className="h-5 w-5" /></div>
                                <input type="text" name="surname" placeholder="Soyadınız" value={formData.surname} onChange={handleChange} disabled={loading} required className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20" />
                            </div>
                        </div>
                    </div>

                    {/* Telefon Numarası */}
                    <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Telefon Numarası</label>
                    <div className="mb-1 flex gap-3">
                        <div className="relative w-[130px]">
                            <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} disabled={loading} className="flex h-12 w-full appearance-none items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:bg-navy-900 dark:hover:border-white/20">
                                {countries.map((c) => (<option key={c.code} value={c.code} className="dark:bg-navy-900">{c.flag} {c.code}</option>))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400"><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg></div>
                        </div>
                        <div className="relative flex-1">
                            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"><MdOutlinePhone className="h-5 w-5" /></div>
                            <input type="tel" name="phone" placeholder={placeholder} value={formData.phone} onChange={handlePhoneChange} disabled={loading} required className={`flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 py-3 pl-10 pr-3 text-sm outline-none transition-all dark:text-white ${formData.phone && !phoneValid ? 'border-red-500' : 'border-gray-200 hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:hover:border-white/20'}`} />
                        </div>
                    </div>
                    {formData.phone && !phoneValid && <p className="mb-4 ml-1.5 text-xs text-red-500">Geçersiz Telefon Numarası (örn: 5551234567).</p>}
                    {!formData.phone && <div className="mb-4"></div>}

                    {/* Şifre Alanları */}
                    <div className="mb-4 grid grid-cols-2 gap-4">
                        <div>
                            <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Şifre</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"><MdLockOutline className="h-5 w-5" /></div>
                                <input type={showPassword ? "text" : "password"} name="password" placeholder="En az 6 hane" value={formData.password} onChange={handleChange} disabled={loading} required className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 py-3 pl-10 pr-10 text-sm outline-none transition-all hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:text-white dark:hover:border-white/20" />
                                <div className="absolute inset-y-0 right-3 flex cursor-pointer items-center text-gray-400 hover:text-brand-500" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="mb-2 ml-1.5 block text-sm font-medium text-navy-700 dark:text-white">Şifre (Tekrar)</label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400"><MdLockOutline className="h-5 w-5" /></div>
                                <input type={showConfirmPassword ? "text" : "password"} name="password_confirmation" placeholder="Tekrar yazın" value={formData.password_confirmation} onChange={handleChange} disabled={loading} required className={`flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 py-3 pl-10 pr-10 text-sm outline-none transition-all dark:text-white ${formData.password_confirmation.length > 0 && formData.password !== formData.password_confirmation ? 'border-red-500' : 'border-gray-200 hover:border-brand-500 focus:border-brand-500 dark:border-white/10 dark:hover:border-white/20'}`} />
                                <div className="absolute inset-y-0 right-3 flex cursor-pointer items-center text-gray-400 hover:text-brand-500" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                    {showConfirmPassword ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Şifre Gücü */}
                    {strength && formData.password.length > 0 && (
                        <div className="mb-4 px-1.5">
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-700">
                                <div className={`h-full transition-all duration-300 ${strength.score >= 3 ? "bg-green-500" : strength.score === 2 ? "bg-yellow-500" : "bg-red-500"}`} style={{ width: `${(strength.score / 4) * 100}%` }}></div>
                            </div>
                            <p className={`mt-1 text-xs font-medium ${strength.score >= 3 ? "text-green-600 dark:text-green-400" : strength.score === 2 ? "text-yellow-600 dark:text-yellow-400" : "text-red-600 dark:text-red-400"}`}>
                                Şifre Gücü: {strength.label}
                            </p>
                        </div>
                    )}

                    {/* Checkboxlar */}
                    <div className="mb-4 mt-2 flex flex-col gap-3 px-1.5">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} disabled={loading} className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-900 dark:checked:bg-brand-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">
                <button type="button" onClick={() => setPolicyModal({ open: true, type: "terms" })} className="font-medium text-brand-500 hover:underline dark:text-brand-400">Kullanım Koşulları</button>'nı ve <button type="button" onClick={() => setPolicyModal({ open: true, type: "privacy" })} className="font-medium text-brand-500 hover:underline dark:text-brand-400">Gizlilik Politikası</button>'nı kabul ediyorum.
              </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} disabled={loading} className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-900 dark:checked:bg-brand-400" />
                            <span className="text-sm text-gray-600 dark:text-gray-300">Kampanya ve bilgilendirmeleri SMS ile almak istiyorum (isteğe bağlı).</span>
                        </label>
                    </div>

                    {/* Aksiyon Butonu */}
                    <button type="submit" disabled={loading || !formValid} className="linear mt-2 flex w-full items-center justify-center rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200">
                        {loading ? (
                            <>
                                <svg className="mr-2 h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Kayıt yapılıyor…
                            </>
                        ) : "Kayıt Ol"}
                    </button>

                    <div className="mt-4 text-center">
                        <span className="text-sm font-medium text-navy-700 dark:text-gray-400">Zaten hesabınız var mı?</span>
                        <Link to="/auth/sign-in" className="ml-1 text-sm font-bold text-brand-500 hover:text-brand-600 dark:text-brand-400">Giriş yapın</Link>
                    </div>
                </form>
            </div>

            {/* --- Native Tailwind Toasts --- */}
            <div className="fixed top-5 right-5 z-[100] flex flex-col gap-2">
                {toasts.map((t) => (
                    <div key={t.id} className="flex min-w-[300px] items-start gap-3 rounded-xl bg-white p-4 shadow-xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-green-500/20">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-500 dark:bg-green-500/20">✓</div>
                        <div>
                            <h5 className="text-sm font-bold text-navy-700 dark:text-white">{t.title}</h5>
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{t.body}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* --- Native Tailwind Modal (Politikalar) --- */}
            {policyModal.open && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm transition-all">
                    <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800 sm:p-8">
                        <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4 dark:border-white/10">
                            <h3 className="text-xl font-bold text-navy-700 dark:text-white">{renderPolicyTitle}</h3>
                            <button onClick={() => setPolicyModal({ open: false, type: null })} className="rounded-full p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700">
                                <MdClose className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="mb-6 max-h-[50vh] overflow-y-auto pr-2">
                            {renderPolicyBody}
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-white/10">
                            <span className="text-xs text-gray-500">Son güncelleme: 21.10.2025</span>
                            <button onClick={() => setPolicyModal({ open: false, type: null })} className="rounded-xl bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-all dark:bg-brand-400 dark:hover:bg-brand-300">
                                Anladım
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}